import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Eye, Network, Power } from 'lucide-react';

export const SplashScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 300),
      setTimeout(() => setPhase(2), 1200),
      setTimeout(() => setPhase(3), 2200),
      setTimeout(() => setPhase(4), 3200),
      setTimeout(() => setPhase(5), 4800),
      setTimeout(() => onComplete(), 5200)
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  const services = [
    { name: 'ELECTRICAL', icon: Zap },
    { name: 'SECURITY', icon: Eye },
    { name: 'NETWORK', icon: Network },
    { name: 'POWER', icon: Power }
  ];

  return (
    <div className="fixed inset-0 bg-[#0a0a0a] z-[100] flex items-center justify-center overflow-hidden" data-testid="splash-screen">
      {/* Corner accents */}
      <motion.div
        className="absolute top-0 left-0 w-32 h-32"
        initial={{ opacity: 0 }}
        animate={{ opacity: phase >= 1 ? 1 : 0 }}
      >
        <motion.div
          className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-red-600 to-transparent"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          style={{ transformOrigin: 'left' }}
        />
        <motion.div
          className="absolute top-0 left-0 h-full w-[2px] bg-gradient-to-b from-red-600 to-transparent"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          style={{ transformOrigin: 'top' }}
        />
      </motion.div>

      <motion.div
        className="absolute bottom-0 right-0 w-32 h-32"
        initial={{ opacity: 0 }}
        animate={{ opacity: phase >= 1 ? 1 : 0 }}
      >
        <motion.div
          className="absolute bottom-0 right-0 w-full h-[2px] bg-gradient-to-l from-red-600 to-transparent"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          style={{ transformOrigin: 'right' }}
        />
        <motion.div
          className="absolute bottom-0 right-0 h-full w-[2px] bg-gradient-to-t from-red-600 to-transparent"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          style={{ transformOrigin: 'bottom' }}
        />
      </motion.div>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        
        {/* Horizontal reveal line */}
        <motion.div
          className="absolute w-[400px] h-[1px] bg-gradient-to-r from-transparent via-red-500 to-transparent"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ 
            scaleX: phase >= 1 && phase < 2 ? 1 : 0,
            opacity: phase >= 1 && phase < 2 ? 1 : 0
          }}
          transition={{ duration: 0.6 }}
        />

        {/* Logo container */}
        <motion.div
          className="relative mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: phase >= 2 ? 1 : 0,
            scale: phase >= 2 ? 1 : 0.8
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Subtle glow */}
          <motion.div
            className="absolute -inset-16 rounded-full opacity-30"
            style={{
              background: 'radial-gradient(circle, rgba(227,27,35,0.4) 0%, transparent 70%)',
              filter: 'blur(40px)'
            }}
            animate={{
              opacity: phase >= 2 ? [0.2, 0.4, 0.2] : 0,
            }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          
          {/* Logo */}
          <motion.img 
            src="/images/logo.png" 
            alt="BELLAJA" 
            className="h-32 md:h-44 relative z-10"
            initial={{ filter: 'brightness(0)' }}
            animate={{ 
              filter: phase >= 2 ? 'brightness(1)' : 'brightness(0)'
            }}
            transition={{ duration: 0.8 }}
          />
        </motion.div>

        {/* Company name with typing effect */}
        <motion.div
          className="text-center mb-6 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 3 ? 1 : 0 }}
        >
          <motion.div
            className="flex items-center justify-center gap-3 mb-4"
            initial={{ y: 40 }}
            animate={{ y: phase >= 3 ? 0 : 40 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <motion.div 
              className="h-[1px] w-12 bg-gradient-to-r from-transparent to-red-500"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: phase >= 3 ? 1 : 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              style={{ transformOrigin: 'right' }}
            />
            <h2 className="font-heading text-sm md:text-base tracking-[0.4em] text-gray-400 uppercase">
              Electrical & Security Systems
            </h2>
            <motion.div 
              className="h-[1px] w-12 bg-gradient-to-l from-transparent to-red-500"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: phase >= 3 ? 1 : 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              style={{ transformOrigin: 'left' }}
            />
          </motion.div>

          <motion.p
            className="text-gray-500 text-sm tracking-widest"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 3 ? 1 : 0 }}
            transition={{ delay: 0.5 }}
          >
            NEW YORK CITY
          </motion.p>
        </motion.div>

        {/* Services row */}
        <motion.div
          className="flex items-center gap-8 md:gap-12 mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 4 ? 1 : 0 }}
        >
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.name}
                className="flex flex-col items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: phase >= 4 ? 1 : 0,
                  y: phase >= 4 ? 0 : 20
                }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
              >
                <motion.div
                  className="w-12 h-12 md:w-14 md:h-14 border border-gray-700 flex items-center justify-center mb-3 relative overflow-hidden"
                  whileHover={{ borderColor: '#E31B23' }}
                >
                  {/* Hover fill effect */}
                  <motion.div
                    className="absolute inset-0 bg-red-600"
                    initial={{ y: '100%' }}
                    whileHover={{ y: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                  <Icon className="w-5 h-5 md:w-6 md:h-6 text-gray-400 relative z-10" />
                </motion.div>
                <span className="text-[10px] md:text-xs text-gray-500 tracking-wider font-medium">
                  {service.name}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Bottom progress section */}
      <div className="absolute bottom-12 left-0 right-0">
        <div className="max-w-md mx-auto px-8">
          {/* Progress bar */}
          <div className="relative h-[2px] bg-gray-800 overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-600 via-red-500 to-red-600"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 4.8, ease: 'linear' }}
            />
          </div>
          
          {/* Status text */}
          <motion.div
            className="flex items-center justify-between mt-4 text-xs text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
          >
            <span className="tracking-widest uppercase">Loading</span>
            <span className="font-mono">NYC DOB CERTIFIED</span>
          </motion.div>
        </div>
      </div>

      {/* Fade out */}
      <motion.div
        className="absolute inset-0 bg-[#0a0a0a]"
        initial={{ opacity: 0 }}
        animate={{ opacity: phase >= 5 ? 1 : 0 }}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
};
